import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
/**
 * Openable references in rendered text. A detected ref becomes a link only once the run's machine
 * resolved it; until then (and when it does not resolve) it is plain text — never a dead link.
 * Strong refs that do not resolve carry a subtle "not found in worktree" hint.
 */
import { Fragment, useEffect, useMemo, useSyncExternalStore } from 'react';
import { detectRefs, refKey, segmentRefs } from '../lib/refs.js';
import { cx, useViewer } from './primitives.js';
const NOOP = () => () => { };
const ZERO = () => 0;
function useRefState(store, ref) {
    useSyncExternalStore(store?.subscribe ?? NOOP, store?.getVersion ?? ZERO, store?.getVersion ?? ZERO);
    // biome-ignore lint/correctness/useExhaustiveDependencies: keyed by the ref identity
    useEffect(() => {
        store?.want(ref);
    }, [store, refKey(ref)]);
    return store ? store.state(ref) : { status: 'pending' };
}
function missingHint(ref, st) {
    if (ref.strength === 'weak')
        return undefined;
    if (st.status === 'denied')
        return 'Not shown: outside the worktree or a secret file';
    if (st.status !== 'missing')
        return undefined;
    if (st.resolution?.reason === 'live-only')
        return 'Served by a live dev server only; no snapshot available';
    if (ref.kind === 'commit')
        return 'Commit not found in this repository';
    if (ref.kind === 'artifact')
        return 'Artifact not available';
    return 'Not found in worktree';
}
function describe(ref, st) {
    if (st.status === 'ambiguous')
        return `Choose which ${ref.text} to open (${st.resolution?.matches?.length ?? 0} matches)`;
    const t = st.resolution?.target;
    if (!t)
        return `Open ${ref.text}`;
    switch (t.type) {
        case 'file':
            return `Open ${t.path}${ref.line ? ` at line ${ref.line}` : ''}`;
        case 'dir':
            return `Open folder ${t.path || '/'}`;
        case 'commit':
            return `Open commit ${t.sha.slice(0, 7)}`;
        case 'branch':
            return `Open branch ${t.name}`;
        case 'preview':
            return `Open preview of ${t.path || '/'}`;
    }
}
/** One reference. `fallback` renders when there is no resolver (e.g. a plain link). */
export function RefLink({ refValue, children, fallback, className, }) {
    const { refs, renderLivePreview } = useViewer();
    const st = useRefState(refs, refValue);
    if (!refs)
        return _jsx(_Fragment, { children: fallback ?? children });
    // ---- live-preview-tunnel: live-only dev-server URLs open as a live preview ----
    if (renderLivePreview &&
        refValue.kind === 'url' &&
        st.status === 'missing' &&
        st.resolution?.reason === 'live-only')
        return (_jsx("button", { type: "button", className: cx('vk-ref', className), title: `Open a live preview of ${refValue.text}`, "data-ref-kind": "url", onClick: (e) => {
                e.stopPropagation();
                refs.push({
                    kind: 'target',
                    target: { type: 'preview', url: refValue.text, path: '', root: 'worktree' },
                });
            }, children: children }));
    // ---- end live-preview-tunnel ----
    if (st.status === 'resolved' || st.status === 'ambiguous')
        return (_jsx("button", { type: "button", className: cx('vk-ref', st.status === 'ambiguous' && 'vk-ref--ambiguous', className), title: describe(refValue, st), "data-ref-kind": refValue.kind, onClick: (e) => {
                e.stopPropagation();
                refs.openRef(refValue);
            }, children: children }));
    const hint = missingHint(refValue, st);
    return hint ? (_jsx("span", { className: cx('vk-ref-missing', className), title: hint, children: children })) : (children);
}
/** Cheap pre-check before running the detectors on a text node. */
export function mayContainRefs(text) {
    return text.length > 2 && /[./\\]|_[0-9a-f]|[0-9a-f]{7}/.test(text);
}
/** Plain text with any detected references linked (only when a resolver is present). */
export function RefText({ text }) {
    const { refs } = useViewer();
    const parts = useMemo(() => (refs ? segmentRefs(text, detectRefs(text)) : null), [refs, text]);
    if (!parts)
        return _jsx(_Fragment, { children: text });
    return (_jsx(_Fragment, { children: parts.map((p, i) => typeof p === 'string' ? (_jsx(Fragment, { children: p }, i)) : (_jsx(RefLink, { refValue: p.ref, children: text.slice(p.start, p.end) }, `${p.start}:${refKey(p.ref)}`))) }));
}
//# sourceMappingURL=RefLink.js.map